/**
 * Authentication API functions.
 */

import { apiClient } from './client'

export interface User {
  id: string
  email: string
  display_name: string | null
  email_verified: boolean
  notification_frequency: 'immediate' | 'daily' | 'weekly' | 'none'
  created_at: string
}

export interface UpdateProfileData {
  display_name?: string | null
  notification_frequency?: 'immediate' | 'daily' | 'weekly' | 'none'
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
}

export interface MessageResponse {
  message: string
}

export interface RegisterData {
  email: string
  password: string
}

export interface LoginData {
  email: string
  password: string
}

/**
 * Register a new user account.
 */
export async function register(data: RegisterData): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/register', data)
  return response.data
}

/**
 * Log in with email and password.
 */
export async function login(data: LoginData): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/login', data)
  return response.data
}

/**
 * Log out by revoking the refresh token.
 */
export async function logout(refreshToken: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/logout', {
    refresh_token: refreshToken,
  })
  return response.data
}

/**
 * Log out from all devices.
 */
export async function logoutAll(): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/logout-all')
  return response.data
}

/**
 * Refresh access token using refresh token.
 */
export async function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/refresh', {
    refresh_token: refreshToken,
  })
  return response.data
}

/**
 * Verify email with 6-digit code.
 */
export async function verifyEmail(code: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/verify-email', { code })
  return response.data
}

/**
 * Resend verification code.
 */
export async function resendVerification(): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/resend-verification')
  return response.data
}

/**
 * Request password reset.
 */
export async function forgotPassword(email: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/forgot-password', { email })
  return response.data
}

/**
 * Reset password with code.
 */
export async function resetPassword(code: string, newPassword: string): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>('/auth/reset-password', {
    code,
    new_password: newPassword,
  })
  return response.data
}

/**
 * Get current user profile.
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me')
  return response.data
}

/**
 * Update current user profile.
 */
export async function updateProfile(data: UpdateProfileData): Promise<User> {
  const response = await apiClient.patch<User>('/auth/me', data)
  return response.data
}
